import { createRef, useEffect } from "react";
import { Button, Col, Divider, Form, Modal, Row, Select } from "antd";
import _ from "lodash";

import {
  FIELD_KEYS,
  FIELD_LABELS,
  FIELD_ORDERS,
  TABLE_SORTABLE_FIELDS,
} from "constants/UnitFields";
import { FILTER_KEYS, FILTER_LABELS } from "constants/table";
import FormItemSorters from "components/Common/FormItemSorters";
import FormSubmitButton from "components/Common/FormSubmitButton";
import NumberRangeInput from "components/Common/NumberRangeInput";
import useFetch from "hooks/useFetch";

// ###### Constants ######

const { useForm } = Form;

const sortByOptions = _.intersection(FIELD_ORDERS, TABLE_SORTABLE_FIELDS).map(
  (field) => ({
    label: FIELD_LABELS[field],
    value: field,
  })
);

// ###### Component ######

const AdvancedSearchModal = ({
  showModal,
  filters,
  onShowModalChange,
  onFiltersChange,
}) => {
  const [form] = useForm();
  const formRef = createRef();

  const {
    loading: loadingStatusOptions,
    responseData: statusOptionsResponseData,
    doFetch: fetchUnitStatusOptions,
  } = useFetch("/units/getUnitStatusOptions", "GET", []);
  const {
    loading: loadingDetectedCodeOptions,
    responseData: detectedCodeOptionsResponseData,
    doFetch: fetchDetectedCodeOptions,
  } = useFetch("/detectedCodes/getDetectedCodeOptions", "GET", []);

  const statusOptions = statusOptionsResponseData.data ?? [];
  const detectedCodeOptions = detectedCodeOptionsResponseData.data ?? [];

  const closeModal = (reset = true) => {
    onShowModalChange(false);
    if (reset) {
      form.resetFields();
    }
  };

  const handleAdvancedSearch = (values) => {
    onFiltersChange(values);
    closeModal(false);
  };

  useEffect(() => {
    if (!!filters.item) {
      fetchUnitStatusOptions();
      fetchDetectedCodeOptions({
        item: filters.item,
      });
    }
  }, []);

  return (
    <>
      <Modal
        title="Advanced Search"
        open={showModal}
        loading={loadingStatusOptions || loadingDetectedCodeOptions}
        onCancel={closeModal}
        footer={() => (
          <>
            <Button onClick={closeModal}>Cancel</Button>,
            <FormSubmitButton
              form={form}
              label="Search"
              buttonProps={{ onClick: () => formRef.current.submit() }}
            />
          </>
        )}
        wrapClassName="modal-xl-title"
      >
        <Form
          form={form}
          ref={formRef}
          initialValues={filters}
          onFinish={handleAdvancedSearch}
          layout="vertical"
        >
          <Divider orientation="left" orientationMargin="0">
            Filters
          </Divider>
          <Row gutter={[16, 0]}>
            <Col span="12">
              <Form.Item label={FIELD_LABELS[FIELD_KEYS.X]} name={FIELD_KEYS.X}>
                <NumberRangeInput />
              </Form.Item>
            </Col>
            <Col span="12">
              <Form.Item
                label={FIELD_LABELS[FIELD_KEYS.STATUS]}
                name={FIELD_KEYS.STATUS}
              >
                <Select
                  options={statusOptions}
                  optionFilterProp="label"
                  mode="multiple"
                  allowClear={true}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[16, 0]}>
            <Col span="12">
              <Form.Item
                label={FIELD_LABELS[FIELD_KEYS.DETECTED_CODE]}
                name={FIELD_KEYS.DETECTED_CODE}
              >
                <Select
                  options={detectedCodeOptions}
                  optionFilterProp="label"
                  mode="multiple"
                  allowClear={true}
                />
              </Form.Item>
            </Col>
          </Row>
          <Divider orientation="left" orientationMargin="0">
            {FILTER_LABELS[FILTER_KEYS.SORTERS]}
          </Divider>
          <FormItemSorters form={form} sortByOptions={sortByOptions} />
        </Form>
      </Modal>
    </>
  );
};

export default AdvancedSearchModal;
